import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
} from 'firebase/auth';
import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RegistrationProgress from '../components/RegistrationProgress';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEarthAmericas,
  faEnvelope,
  faLocationDot,
  faLock,
  faPhone,
  faShieldHalved,
  faUser,
  faUserShield,
} from '@fortawesome/free-solid-svg-icons';

const OTHER_OPTION = '__other__';

const normalizeLookupValue = (value) => String(value || '').trim().toLowerCase();

const getOptionDisplay = (option) => option?.display || option?.label || '';

const sortOptionsByDisplay = (options) =>
  [...options].sort((a, b) => getOptionDisplay(a).localeCompare(getOptionDisplay(b)));

const StateProvinceIcon = ({ className = '' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M3.5 5.5 8.8 3l6.4 2.5 5.3-2.3v15.3l-5.3 2.3-6.4-2.5-5.3 2.2V5.5Z"
      fill="none"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="1.8"
    />
    <path d="M8.8 3v15.3" stroke="currentColor" strokeWidth="1.8" />
    <path d="M15.2 5.5v15.3" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

function FieldShell({ icon, children }) {
  return (
    <div className="flex gap-3 sm:gap-4">
      <div className="mt-7 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-blue-50 text-blue-600 shadow-sm">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        {children}
      </div>
    </div>
  );
}

const PHONE_COUNTRY_CODES = [
  { region: 'North America', country: 'United States', code: '+1' },
  { region: 'North America', country: 'Canada', code: '+1' },
  { region: 'North America', country: 'Mexico', code: '+52' },
  { region: 'North America', country: 'Greenland', code: '+299' },
  { region: 'North America', country: 'Saint Pierre and Miquelon', code: '+508' },
  { region: 'Caribbean / Central America', country: 'Anguilla', code: '+1 264' },
  { region: 'Caribbean / Central America', country: 'Antigua and Barbuda', code: '+1 268' },
  { region: 'Caribbean / Central America', country: 'Aruba', code: '+297' },
  { region: 'Caribbean / Central America', country: 'Bahamas', code: '+1 242' },
  { region: 'Caribbean / Central America', country: 'Barbados', code: '+1 246' },
  { region: 'Caribbean / Central America', country: 'Belize', code: '+501' },
  { region: 'Caribbean / Central America', country: 'Bermuda', code: '+1 441' },
  { region: 'Caribbean / Central America', country: 'British Virgin Islands', code: '+1 284' },
  { region: 'Caribbean / Central America', country: 'Cayman Islands', code: '+1 345' },
  { region: 'Caribbean / Central America', country: 'Costa Rica', code: '+506' },
  { region: 'Caribbean / Central America', country: 'Cuba', code: '+53' },
  { region: 'Caribbean / Central America', country: 'Curacao', code: '+599' },
  { region: 'Caribbean / Central America', country: 'Dominica', code: '+1 767' },
  { region: 'Caribbean / Central America', country: 'Dominican Republic', code: '+1 809' },
  { region: 'Caribbean / Central America', country: 'Dominican Republic', code: '+1 829' },
  { region: 'Caribbean / Central America', country: 'Dominican Republic', code: '+1 849' },
  { region: 'Caribbean / Central America', country: 'El Salvador', code: '+503' },
  { region: 'Caribbean / Central America', country: 'Grenada', code: '+1 473' },
  { region: 'Caribbean / Central America', country: 'Guadeloupe', code: '+590' },
  { region: 'Caribbean / Central America', country: 'Guatemala', code: '+502' },
  { region: 'Caribbean / Central America', country: 'Haiti', code: '+509' },
  { region: 'Caribbean / Central America', country: 'Honduras', code: '+504' },
  { region: 'Caribbean / Central America', country: 'Jamaica', code: '+1 876' },
  { region: 'Caribbean / Central America', country: 'Jamaica', code: '+1 658' },
  { region: 'Caribbean / Central America', country: 'Martinique', code: '+596' },
  { region: 'Caribbean / Central America', country: 'Montserrat', code: '+1 664' },
  { region: 'Caribbean / Central America', country: 'Nicaragua', code: '+505' },
  { region: 'Caribbean / Central America', country: 'Panama', code: '+507' },
  { region: 'Caribbean / Central America', country: 'Puerto Rico', code: '+1 787' },
  { region: 'Caribbean / Central America', country: 'Puerto Rico', code: '+1 939' },
  { region: 'Caribbean / Central America', country: 'Saint Barthelemy', code: '+590' },
  { region: 'Caribbean / Central America', country: 'Saint Kitts and Nevis', code: '+1 869' },
  { region: 'Caribbean / Central America', country: 'Saint Lucia', code: '+1 758' },
  { region: 'Caribbean / Central America', country: 'Saint Martin', code: '+590' },
  { region: 'Caribbean / Central America', country: 'Saint Vincent and the Grenadines', code: '+1 784' },
  { region: 'Caribbean / Central America', country: 'Sint Maarten', code: '+1 721' },
  { region: 'Caribbean / Central America', country: 'Trinidad and Tobago', code: '+1 868' },
  { region: 'Caribbean / Central America', country: 'Turks and Caicos Islands', code: '+1 649' },
  { region: 'Caribbean / Central America', country: 'U.S. Virgin Islands', code: '+1 340' },
  { region: 'South America', country: 'Argentina', code: '+54' },
  { region: 'South America', country: 'Bolivia', code: '+591' },
  { region: 'South America', country: 'Brazil', code: '+55' },
  { region: 'South America', country: 'Chile', code: '+56' },
  { region: 'South America', country: 'Colombia', code: '+57' },
  { region: 'South America', country: 'Ecuador', code: '+593' },
  { region: 'South America', country: 'Falkland Islands', code: '+500' },
  { region: 'South America', country: 'French Guiana', code: '+594' },
  { region: 'South America', country: 'Guyana', code: '+592' },
  { region: 'South America', country: 'Paraguay', code: '+595' },
  { region: 'South America', country: 'Peru', code: '+51' },
  { region: 'South America', country: 'Suriname', code: '+597' },
  { region: 'South America', country: 'Uruguay', code: '+598' },
  { region: 'South America', country: 'Venezuela', code: '+58' },
  { region: 'Europe', country: 'Albania', code: '+355' },
  { region: 'Europe', country: 'Andorra', code: '+376' },
  { region: 'Europe', country: 'Austria', code: '+43' },
  { region: 'Europe', country: 'Belarus', code: '+375' },
  { region: 'Europe', country: 'Belgium', code: '+32' },
  { region: 'Europe', country: 'Bosnia and Herzegovina', code: '+387' },
  { region: 'Europe', country: 'Bulgaria', code: '+359' },
  { region: 'Europe', country: 'Croatia', code: '+385' },
  { region: 'Europe', country: 'Cyprus', code: '+357' },
  { region: 'Europe', country: 'Czech Republic', code: '+420' },
  { region: 'Europe', country: 'Denmark', code: '+45' },
  { region: 'Europe', country: 'Estonia', code: '+372' },
  { region: 'Europe', country: 'Faroe Islands', code: '+298' },
  { region: 'Europe', country: 'Finland', code: '+358' },
  { region: 'Europe', country: 'France', code: '+33' },
  { region: 'Europe', country: 'Germany', code: '+49' },
  { region: 'Europe', country: 'Gibraltar', code: '+350' },
  { region: 'Europe', country: 'Greece', code: '+30' },
  { region: 'Europe', country: 'Greenland', code: '+299' },
  { region: 'Europe', country: 'Guernsey', code: '+44' },
  { region: 'Europe', country: 'Hungary', code: '+36' },
  { region: 'Europe', country: 'Iceland', code: '+354' },
  { region: 'Europe', country: 'Ireland', code: '+353' },
  { region: 'Europe', country: 'Isle of Man', code: '+44' },
  { region: 'Europe', country: 'Italy', code: '+39' },
  { region: 'Europe', country: 'Jersey', code: '+44' },
  { region: 'Europe', country: 'Kosovo', code: '+383' },
  { region: 'Europe', country: 'Latvia', code: '+371' },
  { region: 'Europe', country: 'Liechtenstein', code: '+423' },
  { region: 'Europe', country: 'Lithuania', code: '+370' },
  { region: 'Europe', country: 'Luxembourg', code: '+352' },
  { region: 'Europe', country: 'Malta', code: '+356' },
  { region: 'Europe', country: 'Moldova', code: '+373' },
  { region: 'Europe', country: 'Monaco', code: '+377' },
  { region: 'Europe', country: 'Montenegro', code: '+382' },
  { region: 'Europe', country: 'Netherlands', code: '+31' },
  { region: 'Europe', country: 'North Macedonia', code: '+389' },
  { region: 'Europe', country: 'Norway', code: '+47' },
  { region: 'Europe', country: 'Poland', code: '+48' },
  { region: 'Europe', country: 'Portugal', code: '+351' },
  { region: 'Europe', country: 'Romania', code: '+40' },
  { region: 'Europe', country: 'San Marino', code: '+378' },
  { region: 'Europe', country: 'Serbia', code: '+381' },
  { region: 'Europe', country: 'Slovakia', code: '+421' },
  { region: 'Europe', country: 'Slovenia', code: '+386' },
  { region: 'Europe', country: 'Spain', code: '+34' },
  { region: 'Europe', country: 'Sweden', code: '+46' },
  { region: 'Europe', country: 'Switzerland', code: '+41' },
  { region: 'Europe', country: 'Ukraine', code: '+380' },
  { region: 'Europe', country: 'United Kingdom', code: '+44' },
  { region: 'Europe', country: 'Vatican City', code: '+39' },
];

function LocationSelect({
  label,
  required = false,
  value,
  customValue,
  options,
  placeholder,
  customPlaceholder,
  addNewLabel,
  disabled = false,
  onSelect,
  onCustomChange,
}) {
  const isOther = value === OTHER_OPTION;

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <label className="block text-sm font-semibold text-gray-900">
          {label}{required ? ' *' : ''}
        </label>
      </div>
      <select
        value={value}
        onChange={(event) => onSelect(event.target.value)}
        disabled={disabled}
        className="input block w-full min-h-[44px] border border-gray-300/80 bg-white shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-100 disabled:bg-gray-100 disabled:text-gray-500"
      >
        <option value="">{placeholder}</option>
        {options.map((option) => {
          const display = getOptionDisplay(option);
          return (
            <option key={option.id || display} value={display}>
              {display}
            </option>
          );
        })}
        <option
          value={OTHER_OPTION}
          className="bg-blue-50 font-semibold text-blue-700"
          style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', fontWeight: 600 }}
        >
          {addNewLabel}
        </option>
      </select>

      {isOther && (
        <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm font-medium text-amber-800">
            Before adding a new entry, please confirm it is not already listed.
          </p>
          <input
            type="text"
            value={customValue}
            onChange={(event) => onCustomChange(event.target.value)}
            placeholder={customPlaceholder}
            className="input block w-full min-h-[44px] border border-gray-300/80 bg-white shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
          />
        </div>
      )}
    </div>
  );
}

export default function Register() {
  const navigate = useNavigate();
  const textInputClass = 'input block w-full min-h-[44px] border border-gray-300/80 bg-white shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-100';
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('+');
  const [showPhoneCodePicker, setShowPhoneCodePicker] = useState(false);
  const [phoneCodeSearch, setPhoneCodeSearch] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleInitial, setMiddleInitial] = useState('');
  const [lastName, setLastName] = useState('');
  const [country, setCountry] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [city, setCity] = useState('');
  const [customCountry, setCustomCountry] = useState('');
  const [customStateProvince, setCustomStateProvince] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateProvinceOptions, setStateProvinceOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [isRegistering, setIsRegistering] = useState(false);

  const resolvedCountry = country === OTHER_OPTION ? customCountry.trim() : country.trim();
  const resolvedStateProvince =
    stateProvince === OTHER_OPTION ? customStateProvince.trim() : stateProvince.trim();
  const resolvedCity = city === OTHER_OPTION ? customCity.trim() : city.trim();

  useEffect(() => {
    let cancelled = false;

    const loadCountryOptions = async () => {
      try {
        const optionsRef = collection(db, 'directoryOptions', 'countries', 'values');
        const snap = await getDocs(query(optionsRef, orderBy('lower'), limit(100)));
        const options = sortOptionsByDisplay(
          snap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
        );

        if (!cancelled) setCountryOptions(options);
      } catch (err) {
        console.error('Failed to load country options:', err);
        if (!cancelled) setCountryOptions([]);
      }
    };

    loadCountryOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const countryLower = normalizeLookupValue(resolvedCountry);

    const loadStateProvinceOptions = async () => {
      if (!countryLower) {
        setStateProvinceOptions([]);
        return;
      }

      try {
        const optionsRef = collection(db, 'directoryOptions', 'stateProvinces', 'values');
        const snap = await getDocs(
          query(
            optionsRef,
            where('countryLower', '==', countryLower),
            limit(100)
          )
        );
        const options = sortOptionsByDisplay(
          snap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
        );

        if (!cancelled) setStateProvinceOptions(options);
      } catch (err) {
        console.error('Failed to load state/province options:', err);
        if (!cancelled) setStateProvinceOptions([]);
      }
    };

    loadStateProvinceOptions();
    return () => {
      cancelled = true;
    };
  }, [resolvedCountry]);

  useEffect(() => {
    let cancelled = false;
    const countryLower = normalizeLookupValue(resolvedCountry);
    const stateProvinceLower = normalizeLookupValue(resolvedStateProvince);

    const loadCityOptions = async () => {
      if (!countryLower || !stateProvinceLower) {
        setCityOptions([]);
        return;
      }

      try {
        const optionsRef = collection(db, 'directoryOptions', 'cities', 'values');
        const snap = await getDocs(
          query(
            optionsRef,
            where('countryLower', '==', countryLower),
            where('stateProvinceLower', '==', stateProvinceLower),
            limit(100)
          )
        );
        const options = sortOptionsByDisplay(
          snap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
        );

        if (!cancelled) setCityOptions(options);
      } catch (err) {
        console.error('Failed to load city options:', err);
        if (!cancelled) setCityOptions([]);
      }
    };

    loadCityOptions();
    return () => {
      cancelled = true;
    };
  }, [resolvedCountry, resolvedStateProvince]);

  const handleCountrySelect = (nextCountry) => {
    setCountry(nextCountry);
    setCustomCountry('');
    clearStateProvinceAndCity();
  };

  const clearStateProvinceAndCity = () => {
    setStateProvince('');
    setCustomStateProvince('');
    setCity('');
    setCustomCity('');
    setStateProvinceOptions([]);
    setCityOptions([]);
  };

  const handleStateProvinceSelect = (nextStateProvince) => {
    setStateProvince(nextStateProvince);
    setCustomStateProvince('');
    clearCity();
  };

  const clearCity = () => {
    setCity('');
    setCustomCity('');
    setCityOptions([]);
  };

  const handleCustomCountryChange = (nextCountry) => {
    setCustomCountry(nextCountry);
    clearStateProvinceAndCity();
  };

  const handleCustomStateProvinceChange = (nextStateProvince) => {
    setCustomStateProvince(nextStateProvince);
    clearCity();
  };

  const handleCitySelect = (nextCity) => {
    setCity(nextCity);
    setCustomCity('');
  };

  const filteredPhoneCodes = PHONE_COUNTRY_CODES.filter(({ region, country, code }) => {
    const search = phoneCodeSearch.trim().toLowerCase();
    if (!search) return true;
    return `${region} ${country} ${code}`.toLowerCase().includes(search);
  });

  const handlePhoneInputChange = (e) => {
    const digitsAndSeparators = String(e.target.value || '')
      .replace(/[^\d+()\-\s.]/g, '')
      .slice(0, 24);
    setPhone(`+${digitsAndSeparators.replace(/^\s*\+?/, '')}`);
  };

  const handlePhoneCodeSelect = (code) => {
    const currentPhone = String(phone || '+');
    const knownCodes = [...new Set(PHONE_COUNTRY_CODES.map((entry) => entry.code))]
      .sort((a, b) => b.length - a.length);
    const currentCode = knownCodes.find((knownCode) => (
      currentPhone === knownCode || currentPhone.startsWith(`${knownCode} `)
    ));
    const withoutCurrentPrefix = currentCode
      ? currentPhone.slice(currentCode.length).trimStart()
      : currentPhone.replace(/^\+/, '').trimStart();

    setPhone(`${code}${withoutCurrentPrefix ? ` ${withoutCurrentPrefix}` : ' '}`.slice(0, 24));
    setShowPhoneCodePicker(false);
    setPhoneCodeSearch('');
  };

  const validateForm = () => {
    if (
      !email.trim() ||
      !phone.trim() ||
      !password ||
      !confirmPassword ||
      !firstName.trim() ||
      !lastName.trim() ||
      !resolvedCountry ||
      !resolvedCity
    ) {
      toast.error('Please complete all required fields.');
      return false;
    }

    if (!String(email).trim().includes('@')) {
      toast.error('Please enter a valid email address.');
      return false;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return false;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return false;
    }

    return true;
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    let userCred;

    try {
      setIsRegistering(true);
      const normalizedEmail = email.trim().toLowerCase();
      const trimmedFirstName = firstName.trim();
      const trimmedMiddleInitial = middleInitial.trim().slice(0, 1).toUpperCase();
      const trimmedLastName = lastName.trim();
      const trimmedCountry = resolvedCountry;
      const trimmedStateProvince = resolvedStateProvince;
      const trimmedCity = resolvedCity;

      userCred = await createUserWithEmailAndPassword(
        auth,
        normalizedEmail,
        password
      );

      await new Promise((resolve) => {
        if (auth.currentUser) {
          resolve();
          return;
        }

        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
          if (currentUser) {
            unsubscribe();
            resolve();
          }
        });
      });

      let userData;

      await runTransaction(db, async (tx) => {
        const counterRef = doc(db, 'meta', 'counters');
        const userRef = doc(db, 'users', userCred.user.uid);
        const counterSnap = await tx.get(counterRef);
        const lastUserNumber = counterSnap.exists()
          ? Number(counterSnap.data().lastUserNumber || 0)
          : 0;
        const nextUserNumber = lastUserNumber + 1;

        userData = {
        email: normalizedEmail,
        phoneNumber: phone.trim(),
        firstName: trimmedFirstName,
        middleInitial: trimmedMiddleInitial,
        lastName: trimmedLastName,
        lastNameLower: trimmedLastName.toLowerCase(),
        firstInitialLastName: `${trimmedFirstName.charAt(0)}.${trimmedLastName}`,
        country: trimmedCountry,
        countryLower: trimmedCountry.toLowerCase(),
        state: trimmedStateProvince,
        stateProvince: trimmedStateProvince,
        stateProvinceLower: trimmedStateProvince.toLowerCase(),
        city: trimmedCity,
        cityLower: trimmedCity.toLowerCase(),
        notificationPrefs: {},
        userNumber: nextUserNumber,
        joined: serverTimestamp(),
        verified: false,
        phoneVerified: false,
        verificationStatus: null,
        accountSuspended: false,
        accountDisabled: false,
      };

        tx.set(userRef, userData);
        tx.set(counterRef, { lastUserNumber: nextUserNumber }, { merge: true });
      });

      localStorage.setItem(
        'currentUserProfile',
        JSON.stringify({
          ...userData,
          uid: userCred.user.uid,
          joined: new Date().toISOString(),
        })
      );

      await sendEmailVerification(userCred.user);

      toast.success(
        'Registration successful. A verification link has been sent to your email.'
      );
      localStorage.setItem('pendingEmail', normalizedEmail);
      navigate('/verify-email', { replace: true });
    } catch (err) {
      console.error('Registration error:', err);

      if (userCred?.user) {
        try {
          await userCred.user.delete();
        } catch (deleteErr) {
          console.error('Failed to delete auth user after registration error:', deleteErr);
        }
      }

      toast.error(err?.message || 'Registration failed.');
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex justify-center px-4 py-8 sm:py-12">
      <div className="w-full max-w-3xl rounded-2xl bg-white p-5 shadow-xl shadow-blue-100/40 sm:p-7">
        <RegistrationProgress currentStep="register" className="mb-5" />
        <div className="flex flex-col items-center mb-5 text-center">
          <img
            src="/coldwire-title.png"
            onError={(event) => { event.currentTarget.src = '/icon.png'; }}
            alt="ColdWire"
            className="mb-3 h-auto w-full max-w-sm object-contain"
          />
          <h1 className="text-3xl font-bold text-slate-500">Registration</h1>
        </div>

        <div className="mb-6 overflow-hidden rounded-xl border border-slate-100 bg-white shadow-lg shadow-slate-200/60">
          <div className="flex gap-4 border-b border-slate-100 p-4 last:border-b-0">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <FontAwesomeIcon icon={faShieldHalved} className="text-xl" />
            </div>
            <p className="self-center text-base text-slate-800">
              Verified investigators and related professionals.
            </p>
          </div>
          <div className="flex gap-4 border-b border-slate-100 p-4 last:border-b-0">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <FontAwesomeIcon icon={faUserShield} className="text-xl" />
            </div>
            <p className="self-center text-base text-slate-800">
              The email and phone number provided during registration will be verified.
            </p>
          </div>
          <div className="flex gap-4 border-b border-slate-100 p-4 last:border-b-0">
            <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <FontAwesomeIcon icon={faEnvelope} className="text-xl" />
              <FontAwesomeIcon icon={faLock} className="absolute bottom-2 right-2 text-[0.65rem]" />
            </div>
            <p className="self-center text-base text-slate-800">
              Your registration will be reviewed by an administrator before account access is approved.
            </p>
          </div>
        </div>

        <form onSubmit={handleRegister} className="grid grid-cols-1 gap-4 rounded-xl border border-slate-100 bg-white p-4 shadow-lg shadow-slate-200/60 sm:p-5">
          <FieldShell icon={<FontAwesomeIcon icon={faEnvelope} className="text-xl" />}>
            <div className="space-y-2">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-gray-900">Email *</p>
              </div>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={textInputClass}
                autoComplete="email"
              />
            </div>
          </FieldShell>

          <FieldShell icon={<FontAwesomeIcon icon={faPhone} className="text-xl" />}>
            <div className="space-y-2">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-gray-900">Phone Number *</p>
                <p className="text-sm text-gray-600">
                  <strong>Important:</strong> Include your{' '}
                  <button
                    type="button"
                    onClick={() => setShowPhoneCodePicker((prev) => !prev)}
                    className="font-semibold text-blue-600 underline"
                  >
                    country code
                  </button>
                  .
                </p>
              </div>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="+1 555 123 4567"
                value={phone}
                onChange={handlePhoneInputChange}
                className={textInputClass}
              />
              {showPhoneCodePicker && (
                <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
                  <input
                    type="search"
                    value={phoneCodeSearch}
                    onChange={(event) => setPhoneCodeSearch(event.target.value)}
                    placeholder="Search country or code"
                    className="input mb-2 block w-full min-h-[44px] border border-gray-300/80 bg-white shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                  />
                  <div className="max-h-56 overflow-y-auto rounded border border-gray-100">
                    {filteredPhoneCodes.map(({ region, country: phoneCountry, code }) => (
                      <button
                        key={`${region}-${phoneCountry}-${code}`}
                        type="button"
                        onClick={() => handlePhoneCodeSelect(code)}
                        className="flex min-h-[44px] w-full items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-blue-50"
                      >
                        <span>
                          <span className="font-medium text-gray-900">{phoneCountry}</span>
                          <span className="block text-xs text-gray-500">{region}</span>
                        </span>
                        <span className="shrink-0 font-semibold text-blue-700">{code}</span>
                      </button>
                    ))}
                    {filteredPhoneCodes.length === 0 && (
                      <div className="px-3 py-3 text-sm text-gray-500">No country codes found.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </FieldShell>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FieldShell icon={<FontAwesomeIcon icon={faLock} className="text-xl" />}>
              <div className="space-y-2">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-gray-900">Password *</p>
                  <p className="text-sm text-gray-600">Create a password with at least 6 characters.</p>
                </div>
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={textInputClass}
                  autoComplete="new-password"
                />
              </div>
            </FieldShell>

            <FieldShell icon={<FontAwesomeIcon icon={faLock} className="text-xl" />}>
              <div className="space-y-2">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-gray-900">Confirm Password *</p>
                </div>
                <input
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={textInputClass}
                  autoComplete="new-password"
                />
              </div>
            </FieldShell>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FieldShell icon={<FontAwesomeIcon icon={faUser} className="text-xl" />}>
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-900">First Name *</p>
                <input
                  type="text"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={textInputClass}
                  autoComplete="given-name"
                />
              </div>
            </FieldShell>

            <FieldShell icon={<FontAwesomeIcon icon={faUser} className="text-xl" />}>
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-900">Middle Initial</p>
                <input
                  type="text"
                  placeholder="Middle Initial"
                  value={middleInitial}
                  onChange={(e) => setMiddleInitial(e.target.value)}
                  className={textInputClass}
                  maxLength={1}
                  autoComplete="additional-name"
                />
              </div>
            </FieldShell>
          </div>

          <FieldShell icon={<FontAwesomeIcon icon={faUser} className="text-xl" />}>
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-900">Last Name *</p>
              <input
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={textInputClass}
                autoComplete="family-name"
              />
            </div>
          </FieldShell>

          <FieldShell icon={<FontAwesomeIcon icon={faEarthAmericas} className="text-xl" />}>
            <LocationSelect
              label="Country"
              required
              value={country}
              customValue={customCountry}
              options={countryOptions}
              placeholder="Select Country"
              customPlaceholder="Enter country"
              addNewLabel="Add new country"
              onSelect={handleCountrySelect}
              onCustomChange={handleCustomCountryChange}
            />
          </FieldShell>

          <FieldShell icon={<StateProvinceIcon className="h-6 w-6" />}>
            <LocationSelect
              label="State / Province / Region"
              value={stateProvince}
              customValue={customStateProvince}
              options={stateProvinceOptions}
              placeholder="Select State / Province / Region"
              customPlaceholder="Enter state, province, or region"
              addNewLabel="Add new state/province"
              disabled={!resolvedCountry}
              onSelect={handleStateProvinceSelect}
              onCustomChange={handleCustomStateProvinceChange}
            />
          </FieldShell>

          <FieldShell icon={<FontAwesomeIcon icon={faLocationDot} className="text-xl" />}>
            <LocationSelect
              label="City"
              required
              value={city}
              customValue={customCity}
              options={cityOptions}
              placeholder="Select City"
              customPlaceholder="Enter city"
              addNewLabel="Add new city"
              disabled={!resolvedCountry || !resolvedStateProvince}
              onSelect={handleCitySelect}
              onCustomChange={setCustomCity}
            />
          </FieldShell>

          <button
            type="submit"
            disabled={isRegistering}
            className={`mt-2 min-h-[52px] rounded-lg bg-blue-600 px-4 py-3 text-base font-semibold text-white shadow-md shadow-blue-200 ${
              isRegistering ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
            }`}
          >
            {isRegistering ? 'Registering...' : 'Register'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-blue-600 underline text-sm min-h-[44px]"
          >
            Already have an account? Log in
          </button>
        </form>
      </div>
      <ToastContainer />
    </div>
  );
}
